import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import startCase from 'lodash/startCase'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Helmet } from 'react-helmet'
import { toast } from 'react-toastify'

import { CREATE, GET_LIST, GET_ONE, useClient } from '@peracto/client'
import { useConfig } from '@peracto/peracto-config'
import { Heading, HydraTable, TimeAgo } from '@peracto/peracto-ui'

const EXPORT_TYPE = 'order_revenue'
const DEFAULT_ERROR_MESSAGE = 'Whoops, there was a problem...'
const POLL_INTERVAL_MS = 30 * 1000
const TWENTY_FOUR_H_MS = 24 * 60 * 60 * 1000

const today = () => moment().startOf('day').toDate()

const defaultAfterDate = () => moment().subtract(1, 'month').startOf('month').toDate()
const defaultBeforeDate = () => moment().subtract(1, 'month').endOf('month').startOf('day').toDate()

const maxBeforeDate = (afterDate) => {
    const yearOut = moment(afterDate).add(2, 'years').subtract(1, 'day').toDate()
    const todayDate = today()
    return yearOut < todayDate ? yearOut : todayDate
}

const isCreatedWithinLast24Hours = (createdAt) => {
    if (!createdAt) return false
    const t = new Date(createdAt).getTime()
    if (Number.isNaN(t)) return false
    return t >= Date.now() - TWENTY_FOUR_H_MS
}

function hydraOrGenericMessage(e) {
    return e?.error?.body?.hasOwnProperty('hydra:description')
        ? e.error.body['hydra:description']
        : null
}

const OrderRevenueExportList = () => {
    const { client } = useClient()
    const config = useConfig()
    const formatDate = config.get('user_format_date', config.get('format_date'))

    const [afterDate, setAfterDate] = useState(defaultAfterDate)
    const [beforeDate, setBeforeDate] = useState(defaultBeforeDate)
    const [creatingExport, setCreatingExport] = useState(false)
    const [tableKey, setTableKey] = useState(0)
    const [listRows, setListRows] = useState([])
    const [selectedColumns, onSelectColumn] = useState({})

    const shouldPoll = useMemo(
        () =>
            listRows.some(
                (row) => row?.status === 'processing' && isCreatedWithinLast24Hours(row?.createdAt)
            ),
        [listRows]
    )

    const downloadExport = useCallback(
        async (id) => {
            try {
                const { data: downloadData } = await client(GET_ONE, 'export-logs', {
                    id: `/export-logs/${id}/pre-signed-url`,
                })

                if (downloadData?.downloadUrl) {
                    window.location.href = downloadData.downloadUrl
                } else {
                    toast.error(DEFAULT_ERROR_MESSAGE)
                }
            } catch (e) {
                console.error(e)
                const status = e?.status ?? e?.error?.status ?? e?.response?.status
                const hydraDescription = hydraOrGenericMessage(e)

                if (status === 400) {
                    toast.error(
                        hydraDescription ?? 'Export is not ready to download yet. Please try again.'
                    )
                } else {
                    toast.error(hydraDescription ?? DEFAULT_ERROR_MESSAGE)
                }
            }
        },
        [client]
    )

    const columns = useMemo(
        () => [
            {
                Header: 'ID',
                id: 'exportId',
                accessor: 'originId',
                disableSorting: true,
                Cell: ({ cell: { value }, row }) => (
                    <span data-testid={`row-${row.index}-id`}>{value}</span>
                ),
                selectable: false,
            },
            {
                Header: 'Type',
                accessor: 'type',
                sortType: 'basic',
                Cell: ({ cell: { value } }) => startCase((value || '').replaceAll('_', ' ')),
                selectable: false,
                disableSorting: true,
            },
            {
                Header: 'Status',
                accessor: 'status',
                disableSorting: true,
                Cell: ({ cell: { value } }) => (
                    <span
                        className={classNames('badge', 'badge-pill', {
                            'badge-info': value === 'processing',
                            'badge-success': value === 'complete',
                            'badge-danger': value === 'error',
                        })}
                    >
                        {startCase(value)}
                    </span>
                ),
                selectable: false,
            },
            {
                Header: 'Row Count',
                accessor: 'rowCount',
                disableSorting: true,
                Cell: ({ cell: { value } }) => (value != null ? value : '-'),
                selectable: false,
            },
            {
                Header: 'Created',
                accessor: 'createdAt',
                disableSorting: true,
                Cell: ({ cell: { value } }) => {
                    if (!value) return '-'
                    const date = new Date(value)
                    if (!date.getDate()) return '-'
                    return <TimeAgo title={formatDate(date)} date={value} />
                },
                selectable: false,
            },
            {
                Header: 'Actions',
                id: 'actions',
                accessor: () => null,
                disableSorting: true,
                Cell: ({ row }) => {
                    if (row.original?.status === 'complete') {
                        return (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => downloadExport(row.original.originId)}
                                data-testid={`row-${row.index}-download`}
                            >
                                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                                Download
                            </button>
                        )
                    }
                    return '-'
                },
                selectable: false,
            },
        ],
        [formatDate, downloadExport]
    )

    const onFetchData = useCallback(
        async (options) => {
            const result = await client(GET_LIST, 'export-logs', options)
            setListRows(Array.isArray(result?.data) ? result.data : [])
            return result
        },
        [client]
    )

    useEffect(() => {
        if (!shouldPoll) return undefined

        const id = setInterval(() => {
            setTableKey((k) => k + 1)
        }, POLL_INTERVAL_MS)

        return () => clearInterval(id)
    }, [shouldPoll])

    const onAfterDateChange = (date) => {
        setAfterDate(date)
        const max = maxBeforeDate(date)
        if (!beforeDate || beforeDate < date) {
            setBeforeDate(date)
        } else if (beforeDate > max) {
            setBeforeDate(max)
        }
    }

    const createExport = async () => {
        setCreatingExport(true)
        try {
            const formattedAfter = moment(afterDate).format('YYYY-MM-DD') + 'T00:00:00+00:00'
            const formattedBefore = moment(beforeDate).format('YYYY-MM-DD') + 'T23:59:59+00:00'

            await client(CREATE, 'export-logs', {
                data: {
                    type: EXPORT_TYPE,
                    options: [
                        {
                            type: 'createdAt',
                            value: JSON.stringify({ after: formattedAfter, before: formattedBefore }),
                        },
                    ],
                },
            })
            toast.success('Order revenue export started successfully!')
            setTableKey((k) => k + 1)
        } catch (e) {
            console.error(e)
            toast.error(hydraOrGenericMessage(e) ?? DEFAULT_ERROR_MESSAGE)
        } finally {
            setCreatingExport(false)
        }
    }

    return (
        <>
            <Helmet>
                <title>Order Revenue Export | Peracto</title>
            </Helmet>
            <Heading name="Order Revenue Export" />

            <div className="row mb-3">
                <div className="col">
                    <div className="card card--transparent">
                        <div className="card-body">
                            <div className="form-row align-items-end">
                                <div className="col-auto">
                                    <label className="d-block mb-1 font-weight-medium" htmlFor="after-date">
                                        From
                                    </label>
                                    <DatePicker
                                        id="after-date"
                                        className="form-control"
                                        dateFormat="dd/MM/yyyy"
                                        selected={afterDate}
                                        onChange={onAfterDateChange}
                                        selectsStart
                                        startDate={afterDate}
                                        endDate={beforeDate}
                                        maxDate={today()}
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                    />
                                </div>
                                <div className="col-auto">
                                    <label className="d-block mb-1 font-weight-medium" htmlFor="before-date">
                                        To
                                    </label>
                                    <DatePicker
                                        id="before-date"
                                        className="form-control"
                                        dateFormat="dd/MM/yyyy"
                                        selected={beforeDate}
                                        onChange={setBeforeDate}
                                        selectsEnd
                                        startDate={afterDate}
                                        endDate={beforeDate}
                                        minDate={afterDate}
                                        maxDate={maxBeforeDate(afterDate)}
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                    />
                                </div>
                                <div className="col-auto">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={createExport}
                                        disabled={creatingExport || !afterDate || !beforeDate}
                                        data-testid="generate-new-export"
                                    >
                                        <FontAwesomeIcon icon={faDownload} className="mr-2" />
                                        {creatingExport ? 'Generating Export...' : 'Generate Export'}
                                    </button>
                                </div>
                            </div>
                            <small className="text-muted d-block mt-2">
                                Maximum date range is 2 years.
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col">
                    <div className="card card--transparent">
                        <div className="card-body">
                            <HydraTable
                                key={tableKey}
                                columns={columns}
                                selectedColumns={selectedColumns}
                                onFetchData={onFetchData}
                                onSelectColumn={onSelectColumn}
                                alwaysFilter={{ type: EXPORT_TYPE }}
                                noResultsMessage={
                                    <p className="mb-0 text-muted">
                                        No order revenue exports have been created
                                    </p>
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default OrderRevenueExportList
